'use client';

import { useEffect } from 'react';

type UpdateHooks = {
	/** Optional: show a UI and call `activate()` to apply now */
	onUpdateReady?: (activate: () => void) => void;
};

// Example: pass hooks via props if you want a toast.
// For now we inline a no-UI auto-apply behavior.
const hooks: UpdateHooks = {
	onUpdateReady: (activate) => {
		// AUTO-APPLY: immediately activate new SW.
		// Replace with a toast that calls `activate()` on user click.
		activate();
	},
};

const PwaRegistrar = () => {
	useEffect(() => {
		if (typeof window === 'undefined') return;
		if (process.env.NODE_ENV !== 'production') return;
		if (!('serviceWorker' in navigator)) return;

		const swUrl = '/sw.js';

		// Was this tab already SW-controlled when this script ran?
		// If false, it's a first visit (first install) — do NOT reload on controllerchange.
		const wasControlled = !!navigator.serviceWorker.controller;

		// We only reload when we *intentionally* activate a waiting worker (upgrade path).
		let shouldReloadOnControllerChange = false;

		// Reload policy: be polite if the tab is visible.
		const reloadPolitely = () => {
			if (document.visibilityState === 'hidden') {
				location.reload();
			} else if ('requestIdleCallback' in window) {
				// only reload if browser is idle
				(window as any).requestIdleCallback(() => location.reload());
			} else {
				setTimeout(() => location.reload(), 0);
			}
		};

		const register = async () => {
			try {
				const registration = await navigator.serviceWorker.register(swUrl, {
					scope: '/',
				});

				// Helper to activate the waiting worker *now* (upgrade).
				const activateNow = () => {
					if (registration.waiting) {
						shouldReloadOnControllerChange = true; // we expect a controllerchange and WANT to reload
						registration.waiting.postMessage('SKIP_WAITING');
					}
				};

				// If a worker is already waiting at load time:
				// - If this tab was already controlled, it's an *upgrade* → apply (or prompt).
				// - If not controlled, it's the first install → do nothing (no reload).
				if (registration.waiting && wasControlled) {
					hooks.onUpdateReady?.(activateNow);
				}

				// Detect a newly found update during this session.
				const updateFoundHandler = () => {
					const installing = registration.installing;
					if (!installing) return;

					const stateChangeHandler = () => {
						// When the new worker reaches 'installed' and we already have a controller,
						// that means it's an *update* (not a first install). Offer/apply it.
						if (
							installing.state === 'installed' &&
							navigator.serviceWorker.controller
						) {
							hooks.onUpdateReady?.(activateNow);
						}
					};

					installing.addEventListener('statechange', stateChangeHandler);
				};

				registration.addEventListener('updatefound', updateFoundHandler);

				// Only reload when:
				//  1) this tab was controlled before (not a brand-new visit), AND
				//  2) we explicitly decided to activate the new worker (shouldReloadOnControllerChange).
				let refreshing = false;
				const controllerChangeHandler = () => {
					if (refreshing) return;
					if (!wasControlled) return; // ignore first takeover on first visit
					if (!shouldReloadOnControllerChange) return; // only after explicit activation
					refreshing = true;
					reloadPolitely();
				};

				navigator.serviceWorker.addEventListener(
					'controllerchange',
					controllerChangeHandler
				);
			} catch (err) {
				if (process.env.NODE_ENV !== 'production') {
					console.warn('SW registration failed', err);
				}
			}
		};

		// Delay a bit to avoid fighting hydration
		const id = window.setTimeout(register, 1000);
		return () => window.clearTimeout(id);
	}, []);

	return null;
};

export default PwaRegistrar;
